/**
 * Frontend timezone resolver — mirror of the backend `api/src/config/timezone.ts`.
 *
 * The company's IANA timezone is persisted on the Company at signup (derived from
 * `countryCode`) and editable in Company Settings. Date/time formatting reads the
 * ACTIVE company timezone so timestamps render in the tenant's local time,
 * regardless of the viewer's browser timezone — never hardcode Asia/Kolkata.
 *
 * Keep COUNTRY_TIMEZONE in sync with the backend map and with COUNTRY_CURRENCY.
 */
import { useAuthStore } from '../store/authStore';

export const DEFAULT_TIMEZONE = 'Asia/Kolkata';

export const COUNTRY_TIMEZONE: Record<string, string> = {
  in: 'Asia/Kolkata',
  us: 'America/New_York',
  gb: 'Europe/London',
  ae: 'Asia/Dubai',
  sa: 'Asia/Riyadh',
  qa: 'Asia/Qatar',
  om: 'Asia/Muscat',
  bh: 'Asia/Bahrain',
  kw: 'Asia/Kuwait',
  my: 'Asia/Kuala_Lumpur',
  sg: 'Asia/Singapore',
  au: 'Australia/Sydney',
  de: 'Europe/Berlin',
  fr: 'Europe/Paris',
  cn: 'Asia/Shanghai',
  jp: 'Asia/Tokyo',
  kr: 'Asia/Seoul',
  id: 'Asia/Jakarta',
  th: 'Asia/Bangkok',
  lk: 'Asia/Colombo',
  np: 'Asia/Kathmandu',
  bd: 'Asia/Dhaka',
  pk: 'Asia/Karachi',
  za: 'Africa/Johannesburg',
  ng: 'Africa/Lagos',
  ke: 'Africa/Nairobi',
  gh: 'Africa/Accra',
  br: 'America/Sao_Paulo',
  mx: 'America/Mexico_City',
  ru: 'Europe/Moscow',
  it: 'Europe/Rome',
};

/** Curated IANA options for the Settings dropdown (label → value). */
export const TIMEZONE_OPTIONS: { value: string; label: string }[] = [
  { value: 'Asia/Kolkata', label: '(GMT+5:30) India — Kolkata' },
  { value: 'Asia/Dubai', label: '(GMT+4) UAE — Dubai' },
  { value: 'Asia/Riyadh', label: '(GMT+3) Saudi Arabia — Riyadh' },
  { value: 'Asia/Qatar', label: '(GMT+3) Qatar — Doha' },
  { value: 'Asia/Muscat', label: '(GMT+4) Oman — Muscat' },
  { value: 'Asia/Bahrain', label: '(GMT+3) Bahrain — Manama' },
  { value: 'Asia/Kuwait', label: '(GMT+3) Kuwait — Kuwait City' },
  { value: 'Asia/Kuala_Lumpur', label: '(GMT+8) Malaysia — Kuala Lumpur' },
  { value: 'Asia/Singapore', label: '(GMT+8) Singapore' },
  { value: 'Asia/Colombo', label: '(GMT+5:30) Sri Lanka — Colombo' },
  { value: 'Asia/Kathmandu', label: '(GMT+5:45) Nepal — Kathmandu' },
  { value: 'Asia/Dhaka', label: '(GMT+6) Bangladesh — Dhaka' },
  { value: 'Asia/Karachi', label: '(GMT+5) Pakistan — Karachi' },
  { value: 'Asia/Shanghai', label: '(GMT+8) China — Shanghai' },
  { value: 'Asia/Tokyo', label: '(GMT+9) Japan — Tokyo' },
  { value: 'Asia/Seoul', label: '(GMT+9) South Korea — Seoul' },
  { value: 'Asia/Jakarta', label: '(GMT+7) Indonesia — Jakarta' },
  { value: 'Asia/Bangkok', label: '(GMT+7) Thailand — Bangkok' },
  { value: 'Europe/London', label: '(GMT+0/1) UK — London' },
  { value: 'Europe/Berlin', label: '(GMT+1/2) Germany — Berlin' },
  { value: 'Europe/Paris', label: '(GMT+1/2) France — Paris' },
  { value: 'Europe/Rome', label: '(GMT+1/2) Italy — Rome' },
  { value: 'Europe/Moscow', label: '(GMT+3) Russia — Moscow' },
  { value: 'America/New_York', label: '(GMT-5/-4) USA — New York' },
  { value: 'America/Mexico_City', label: '(GMT-6/-5) Mexico — Mexico City' },
  { value: 'America/Sao_Paulo', label: '(GMT-3) Brazil — São Paulo' },
  { value: 'Australia/Sydney', label: '(GMT+10/11) Australia — Sydney' },
  { value: 'Africa/Johannesburg', label: '(GMT+2) South Africa — Johannesburg' },
  { value: 'Africa/Lagos', label: '(GMT+1) Nigeria — Lagos' },
  { value: 'Africa/Nairobi', label: '(GMT+3) Kenya — Nairobi' },
  { value: 'Africa/Accra', label: '(GMT+0) Ghana — Accra' },
];

/** Resolve the default IANA timezone from a country code (case-insensitive). */
export function getTimezoneForCountry(countryCode?: string | null): string {
  if (!countryCode) return DEFAULT_TIMEZONE;
  return COUNTRY_TIMEZONE[countryCode.toLowerCase()] || DEFAULT_TIMEZONE;
}

interface CompanyTimezoneLike {
  countryCode?: string | null;
  timezone?: string | null;
}

/** Prefer the persisted company timezone; fall back to the country default. */
export function resolveCompanyTimezone(company?: CompanyTimezoneLike | null): string {
  if (!company) return DEFAULT_TIMEZONE;
  if (company.timezone) return company.timezone;
  if (company.countryCode) return getTimezoneForCountry(company.countryCode);
  return DEFAULT_TIMEZONE;
}

// ── Module mirror (for non-React callers, e.g. formatDate) ──
// Kept in sync with the auth store so plain helper functions format in the
// active company timezone.
let activeTimezone: string = resolveCompanyTimezone(useAuthStore.getState().user?.company);

useAuthStore.subscribe((state) => {
  activeTimezone = resolveCompanyTimezone(state.user?.company);
});

export function getActiveTimezone(): string {
  return activeTimezone;
}
